import { useState, useEffect } from 'react'
import { useTranslation }    from '../../i18n/LanguageContext'
import { useMobile }          from '../../hooks/useMobile'
import { useTimer }           from '../useTimer'
import { useLandscape }       from '../../hooks/useLandscape'
import { themes }             from '../../api/client'
import GameSetupScreen        from '../GameSetupScreen'
import SaveErrorBanner        from '../SaveErrorBanner'
import { ss }                 from '../gameSetupStyles'
import { useSessionMode }     from '../../professional/SessionModeContext'
import { useGameCompletion }  from '../../hooks/useGameCompletion'
import { useGameSession }     from '../../hooks/useGameSession'
import { useActivitySession } from '../../hooks/useActivitySession'

interface MoneyConfig { mode: MoneyMode; difficulty: Difficulty }
import {
  useMoneyGame,
  type Difficulty,
  type MoneyMode,
  type MoneyGameState,
  type DishItem,
  type MoneyDenomination,
  type McqOption,
} from './useMoneyGame'
import { formatCents }        from './MoneyData'
import GameCompleteScreen from '../GameCompleteScreen'
import { formatGameTime } from '../utils'
import GamePlayHeader from '../GamePlayHeader'
import { COLORS } from '../../styles/tokens'

const THEME_SLUG    = 'eurmoney'
const ACTIVITY_SLUG = 'eurmoney'

const MODES: MoneyMode[]       = ['identify', 'count', 'pay']
const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']

interface MoneyGameProps {
  onBack: () => void
}

type GameScreen = 'setup' | 'playing' | 'complete'

/* ── Root ────────────────────────────────────────────────────── */
export default function MoneyGame({ onBack }: MoneyGameProps) {
  const { t, language }  = useTranslation()
  const m                = t.eurmoney
  const sessionMode      = useSessionMode()
  const { savedSession, saveConfig, clear } = useActivitySession<MoneyConfig>('eurmoney', !sessionMode)
  const restored = savedSession?.config ?? null

  const [screen,     setScreen]     = useState<GameScreen>(sessionMode ? 'playing' : (restored ? 'playing' : 'setup'))
  const [mode,       setMode]       = useState<MoneyMode>((sessionMode?.config.mode as MoneyMode) ?? restored?.mode ?? 'identify')
  const [difficulty, setDifficulty] = useState<Difficulty>((sessionMode?.config.difficulty as Difficulty) ?? restored?.difficulty ?? 'easy')
  const [denoms,     setDenoms]     = useState<MoneyDenomination[]>([])
  const [loading,    setLoading]    = useState(true)
  const [loadError,  setLoadError]  = useState<string | null>(null)

  // Load eurmoney theme images on mount / language change
  useEffect(() => {
    let cancelled = false
    themes.get(THEME_SLUG)
      .then((theme) => {
        if (cancelled) return
        const mapped: MoneyDenomination[] = theme.images
          .filter((img) => img.value != null)
          .map((img) => ({
            id:         img.id,
            imageUrl:   img.url,
            wordEs:     img.wordEs,
            wordEn:     img.wordEn,
            valueCents: img.value!,
          }))
        if (mapped.length === 0) setLoadError(m.noValues)
        setDenoms(mapped)
        setLoading(false)
      })
      .catch(() => { if (!cancelled) { setLoadError('Error al cargar el tema'); setLoading(false) } })
    return () => { cancelled = true }
  }, [language, m.noValues])

  const hook = useMoneyGame(difficulty, mode, denoms, language)
  const { gameComplete, moves, startTime, endTime, correctCount, totalRounds } = hook.state

  const { sessionId } = useGameSession({
    activitySlug: ACTIVITY_SLUG,
    difficulty,
    mode,
    enabled: screen === 'playing',
  })

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete:  gameComplete && totalRounds > 0,
    activitySlug:  ACTIVITY_SLUG,
    difficulty,
    moves,
    startTime,
    endTime,
    pairsTotal:   totalRounds,
    pairsMatched: correctCount,
    sessionId,
    onCompleted:  () => { clear(); setScreen('complete') },
  })

  const handleStart = () => {
    hook.resetGame()
    saveConfig({ mode, difficulty })
    setScreen('playing')
  }

  if (loading) {
    return (
      <div style={s.loadRoot}>
        <span style={s.loadText}>{m.loading}</span>
      </div>
    )
  }

  if (loadError) {
    return (
      <div style={s.loadRoot}>
        <p style={s.errorText}>{loadError}</p>
        <button style={s.backBtnLarge} onClick={onBack}>{m.backToDashboard}</button>
      </div>
    )
  }

  return (
    <div style={s.root}>
      {saveError && <SaveErrorBanner message={t.common.saveError} dismissLabel={t.common.dismissError} onDismiss={clearSaveError} />}
      {screen === 'setup' && (
        <SetupScreen
          mode={mode}
          onModeChange={setMode}
          difficulty={difficulty}
          onDifficultyChange={setDifficulty}
          onStart={handleStart}
          onBack={onBack}
        />
      )}

      {screen === 'playing' && (
        <PlayScreen
          state={hook.state}
          mode={mode}
          onSelectOption={hook.selectOption}
          onAddToDish={hook.addToDish}
          onRemoveFromDish={hook.removeFromDish}
          onClearDish={hook.clearDish}
          onBack={sessionMode ? onBack : () => { clear(); setScreen('setup') }}
        />
      )}

      {screen === 'complete' && (
        <GameCompleteScreen
          emoji="🪙"
          title={m.complete.title}
          subtitle={m.complete.subtitle.replace('{correct}', String(correctCount)).replace('{total}', String(totalRounds))}
          stats={[
            { value: `${correctCount}/${totalRounds}`, label: m.complete.correctLabel },
            { value: String(moves), label: m.complete.movesLabel },
            { value: formatGameTime(startTime, endTime), label: m.complete.timeLabel },
            { value: m.difficulty[difficulty].label, label: m.complete.levelLabel },
          ]}
          playAgainLabel={m.complete.playAgain}
          changeLevelLabel={m.complete.changeLevel}
          backLabel={m.complete.home}
          onPlayAgain={handleStart}
          onChangeLevel={() => { clear(); setScreen('setup') }}
          onBack={onBack}
        />
      )}
    </div>
  )
}

/* ── Setup Screen ────────────────────────────────────────────── */
function SetupScreen({
  mode, onModeChange, difficulty, onDifficultyChange, onStart, onBack,
}: {
  mode:               MoneyMode
  onModeChange:       (m: MoneyMode) => void
  difficulty:         Difficulty
  onDifficultyChange: (d: Difficulty) => void
  onStart:            () => void
  onBack:             () => void
}) {
  const { t } = useTranslation()
  const m     = t.eurmoney

  return (
    <GameSetupScreen
      title={m.title}
      subtitle={m.subtitle}
      backLabel={m.backToDashboard}
      onBack={onBack}
      difficulties={DIFFICULTIES.map((id) => ({ id, ...m.difficulty[id] }))}
      selectedDifficulty={difficulty}
      onDifficultyChange={(id) => onDifficultyChange(id as Difficulty)}
      chooseDifficultyLabel={m.chooseDifficultyTitle}
      startLabel={m.startBtn}
      onStart={onStart}
    >
      {/* Mode selector */}
      <section>
        <h2 style={ss.optionLabel}>{m.chooseModeTitle}</h2>
        <div style={ss.modeGrid}>
          {MODES.map((md) => (
            <button
              key={md}
              style={{ ...ss.modeCard, ...(mode === md ? ss.modeCardActive : {}) }}
              onClick={() => onModeChange(md)}
              aria-pressed={mode === md}
            >
              <span style={s.modeEmoji}>{MODE_EMOJI[md]}</span>
              <span style={s.modeLabel}>{m.modes[md].label}</span>
              <span style={s.modeDesc}>{m.modes[md].description}</span>
            </button>
          ))}
        </div>
      </section>
    </GameSetupScreen>
  )
}

/* ── Play Screen ─────────────────────────────────────────────── */
function PlayScreen({
  state, mode,
  onSelectOption, onAddToDish, onRemoveFromDish, onClearDish, onBack,
}: {
  state:           MoneyGameState
  mode:            MoneyMode
  onSelectOption:  (idx: number, opt: McqOption) => void
  onAddToDish:     (denom: MoneyDenomination) => void
  onRemoveFromDish:(instanceId: string) => void
  onClearDish:     () => void
  onBack:          () => void
}) {
  const { t }      = useTranslation()
  const isMobile   = useMobile()
  const isLandscape = useLandscape()
  const m          = t.eurmoney
  const elapsed    = useTimer(state.startTime, state.endTime)

  const currentRound = state.rounds[state.currentRoundIdx]
  const progress     = state.totalRounds > 0
    ? state.currentRoundIdx / state.totalRounds
    : 0

  // Instruction line
  let instruction = ''
  if (mode === 'identify') instruction = m.instructionIdentify
  else if (mode === 'count') instruction = m.instructionCount
  else if (state.overBudget)    instruction = m.instructionPayOver
  else if (state.payCorrect)    instruction = m.instructionPayCorrect
  else                          instruction = m.instructionPay

  const dishTotal = state.dish.reduce((s, i) => s + i.denom.valueCents, 0)

  return (
    <div style={s.playRoot}>
      <GamePlayHeader
        title={m.title}
        stats={[
          { value: `${state.currentRoundIdx + 1}/${state.totalRounds}`, label: m.round },
          { value: elapsed, label: m.time },
        ]}
        progress={progress * 100}
        progressNow={state.currentRoundIdx}
        progressMax={state.totalRounds}
        onBack={onBack}
        backLabel={m.backToDashboard}
      />

      {/* Game area */}
      <div style={{
        ...s.playContent,
        padding:  isMobile ? '16px' : '24px 32px',
        maxWidth: isLandscape ? (mode === 'pay' ? 960 : 860) : 640,
      }}>

        {/* Identify mode */}
        {mode === 'identify' && currentRound?.type === 'identify' && (
          <IdentifyArea
            denom={currentRound.denom}
            options={currentRound.options}
            correctFlashIdx={state.correctFlashIdx}
            wrongFlashIdx={state.wrongFlashIdx}
            onSelect={onSelectOption}
            isMobile={isMobile}
            isLandscape={isLandscape}
          />
        )}

        {/* Count mode */}
        {mode === 'count' && currentRound?.type === 'count' && (
          <CountArea
            items={currentRound.items}
            options={currentRound.options}
            correctFlashIdx={state.correctFlashIdx}
            wrongFlashIdx={state.wrongFlashIdx}
            onSelect={onSelectOption}
            isMobile={isMobile}
          />
        )}

        {/* Pay mode */}
        {mode === 'pay' && currentRound?.type === 'pay' && (
          <PayArea
            targetCents={currentRound.targetCents}
            wallet={state.wallet}
            dish={state.dish}
            dishTotal={dishTotal}
            payCorrect={state.payCorrect}
            overBudget={state.overBudget}
            onAddToDish={onAddToDish}
            onRemoveFromDish={onRemoveFromDish}
            onClearDish={onClearDish}
            isMobile={isMobile}
            isLandscape={isLandscape}
            m={m}
          />
        )}

        {/* Instruction */}
        <p style={s.instruction}><em>{instruction}</em></p>
      </div>
    </div>
  )
}

/* ── Identify Area ───────────────────────────────────────────── */
function IdentifyArea({
  denom, options, correctFlashIdx, wrongFlashIdx, onSelect, isMobile, isLandscape,
}: {
  denom:           MoneyDenomination
  options:         McqOption[]
  correctFlashIdx: number | null
  wrongFlashIdx:   number | null
  onSelect:        (idx: number, opt: McqOption) => void
  isMobile:        boolean
  isLandscape:     boolean
}) {
  // Portrait: fill most of viewport height. Landscape: fill viewport height (sidebar).
  const imgSize = isLandscape ? 'min(52vh, 44vw)' : isMobile ? 'min(60vw, 48vh)' : 220

  return (
    <div style={{
      ...s.identifyWrap,
      flexDirection: isLandscape ? 'row' : 'column',
      alignItems:    isLandscape ? 'flex-start' : 'center',
    }}>
      <div style={{ ...s.denomImageWrap, flexShrink: 0 }}>
        <img
          src={denom.imageUrl}
          alt={denom.wordEs}
          style={{ width: imgSize, height: imgSize, objectFit: 'contain', borderRadius: 16 }}
        />
      </div>
      <div style={{ ...s.optionsGrid, flex: isLandscape ? 1 : undefined, alignSelf: isLandscape ? 'center' : undefined }}>
        {options.map((opt, idx) => (
          <OptionButton
            key={idx}
            label={opt.label}
            isCorrect={correctFlashIdx === idx ? true : null}
            isWrong={wrongFlashIdx === idx}
            disabled={correctFlashIdx !== null}
            onClick={() => onSelect(idx, opt)}
          />
        ))}
      </div>
    </div>
  )
}

/* ── Count Area ──────────────────────────────────────────────── */
function CountArea({
  items, options, correctFlashIdx, wrongFlashIdx, onSelect, isMobile,
}: {
  items:           MoneyDenomination[]
  options:         McqOption[]
  correctFlashIdx: number | null
  wrongFlashIdx:   number | null
  onSelect:        (idx: number, opt: McqOption) => void
  isMobile:        boolean
}) {
  const coinSize = isMobile ? 104 : 124
  const noteSize = isMobile ? 152 : 178

  return (
    <div style={s.countWrap}>
      <div style={s.countItemsRow}>
        {items.map((item, i) => {
          const sz = item.valueCents >= 500 ? noteSize : coinSize
          return (
            <div key={i} style={s.countItemWrap}>
              <img
                src={item.imageUrl}
                alt={item.wordEs}
                style={{ width: sz, height: sz, objectFit: 'contain', borderRadius: 12 }}
              />
            </div>
          )
        })}
      </div>
      <div style={s.optionsGrid}>
        {options.map((opt, idx) => (
          <OptionButton
            key={idx}
            label={opt.label}
            isCorrect={correctFlashIdx === idx ? true : null}
            isWrong={wrongFlashIdx === idx}
            disabled={correctFlashIdx !== null}
            onClick={() => onSelect(idx, opt)}
          />
        ))}
      </div>
    </div>
  )
}

/* ── Pay Area ────────────────────────────────────────────────── */
function PayArea({
  targetCents, wallet, dish, dishTotal, payCorrect, overBudget,
  onAddToDish, onRemoveFromDish, onClearDish, isMobile, isLandscape, m,
}: {
  targetCents:     number
  wallet:          MoneyDenomination[]
  dish:            DishItem[]
  dishTotal:       number
  payCorrect:      boolean
  overBudget:      boolean
  onAddToDish:     (d: MoneyDenomination) => void
  onRemoveFromDish:(id: string) => void
  onClearDish:     () => void
  isMobile:        boolean
  isLandscape:     boolean
  m:               ReturnType<typeof useTranslation>['t']['eurmoney']
}) {
  const coinSize = isLandscape ? (isMobile ? 90 : 108) : (isMobile ? 82 : 96)

  const dishBorderColor = payCorrect ? COLORS.greenLight : overBudget ? '#E63946' : '#C0A882'

  const priceTag = (
    <div style={s.priceTag}>
      <div style={s.priceTagHole} />
      <span style={s.priceAmount}>{formatCents(targetCents)}</span>
    </div>
  )

  const minColW = coinSize + 20  // coin + horizontal padding buffer
  const walletGrid = (
    <div style={{
      ...s.walletGrid,
      gridTemplateColumns: `repeat(auto-fill, minmax(${minColW}px, 1fr))`,
    }}>
      {wallet.map((denom) => (
        <button
          key={denom.id}
          style={s.walletBtn}
          onClick={() => onAddToDish(denom)}
          disabled={payCorrect}
          aria-label={formatCents(denom.valueCents)}
        >
          <img
            src={denom.imageUrl}
            alt={denom.wordEs}
            style={{ width: coinSize, height: coinSize, objectFit: 'contain', borderRadius: 10 }}
          />
          <span style={s.walletBtnLabel}>{formatCents(denom.valueCents)}</span>
        </button>
      ))}
    </div>
  )

  const dishArea = (
    <div style={{ ...s.dish, borderColor: dishBorderColor }}>
      <div style={s.dishHeader}>
        <span style={s.dishLabel}>{m.dishLabel}</span>
        <span style={s.dishTotal}>
          {m.dishTotal}: <strong>{formatCents(dishTotal)}</strong>
        </span>
        {dish.length > 0 && !payCorrect && (
          <button style={s.clearBtn} onClick={onClearDish} aria-label={m.clearBtn}>
            {m.clearBtn}
          </button>
        )}
      </div>

      {dish.length === 0 ? (
        <p style={s.dishEmpty}>—</p>
      ) : (
        <div style={s.dishItems}>
          {dish.map((item) => (
            <button
              key={item.instanceId}
              style={s.dishItem}
              onClick={() => onRemoveFromDish(item.instanceId)}
              disabled={payCorrect}
              aria-label={`Quitar ${formatCents(item.denom.valueCents)}`}
            >
              <img
                src={item.denom.imageUrl}
                alt={item.denom.wordEs}
                style={{ width: 48, height: 48, objectFit: 'contain', borderRadius: 8 }}
              />
              <span style={s.dishItemLabel}>{formatCents(item.denom.valueCents)}</span>
              {!payCorrect && <span style={s.dishItemRemove}>✕</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )

  if (isLandscape) {
    return (
      <div style={s.payWrapLandscape}>
        <div style={s.payLandscapeLeft}>
          {priceTag}
          {dishArea}
        </div>
        <div style={s.payLandscapeRight}>
          {walletGrid}
        </div>
      </div>
    )
  }

  return (
    <div style={s.payWrap}>
      {priceTag}
      {walletGrid}
      {dishArea}
    </div>
  )
}

/* ── Option Button ───────────────────────────────────────────── */
function OptionButton({
  label, isCorrect, isWrong, disabled, onClick,
}: {
  label:     string
  isCorrect: boolean | null
  isWrong:   boolean
  disabled:  boolean
  onClick:   () => void
}) {
  const bg = isCorrect ? '#D8F3DC' : isWrong ? '#FFE0E3' : '#FFFFFF'
  const border = isCorrect ? `2.5px solid ${COLORS.greenLight}` : isWrong ? '2.5px solid #E63946' : `2px solid ${COLORS.borderWarm}`

  return (
    <button
      className={isWrong ? 'wm-shake' : undefined}
      style={{ ...s.optionBtn, background: bg, border }}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  )
}

/* ── Constants ───────────────────────────────────────────────── */
const MODE_EMOJI: Record<MoneyMode, string> = {
  identify: '🔍',
  count:    '➕',
  pay:      '💰',
}

/* ── Styles ──────────────────────────────────────────────────── */
const s: Record<string, React.CSSProperties> = {
  root: { minHeight: '100dvh', background: COLORS.bgPage },

  // Loading / error
  loadRoot: {
    minHeight:      '100dvh',
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            16,
    background:     COLORS.bgPage,
    padding:        24,
  },
  loadText:    { fontSize: '1.2rem', color: '#6B4C3B' },
  errorText:   { fontSize: '1.1rem', color: '#E63946', textAlign: 'center', maxWidth: 340 },
  backBtnLarge: {
    padding:      '16px 32px',
    fontSize:     '1.1rem',
    fontWeight:   700,
    color:        COLORS.green,
    background:   '#fff',
    border:       `2px solid ${COLORS.green}`,
    borderRadius: 14,
    cursor:       'pointer',
    minHeight:    48,
  },

  // Play root
  playRoot: { minHeight: '100dvh', display: 'flex', flexDirection: 'column', background: COLORS.bgPage },

  // Play content
  playContent: {
    flex:          1,
    display:       'flex',
    flexDirection: 'column',
    gap:           24,
    maxWidth:      640,
    width:         '100%',
    margin:        '0 auto',
  },

  // Instruction
  instruction: {
    fontSize:   '1rem',
    color:      '#6B4C3B',
    textAlign:  'center',
    fontStyle:  'italic',
    minHeight:  24,
  },

  // Identify area
  identifyWrap: { display: 'flex', flexDirection: 'column', gap: 24, alignItems: 'center' },
  denomImageWrap: {
    background:   '#FFFFFF',
    borderRadius: 20,
    padding:      24,
    boxShadow:    '0 4px 20px rgba(27,67,50,0.10)',
    display:      'flex',
    alignItems:   'center',
    justifyContent: 'center',
  },

  // Count area
  countWrap:     { display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center' },
  countItemsRow: {
    display:    'flex',
    flexWrap:   'wrap',
    gap:        12,
    alignItems: 'center',
    justifyContent: 'center',
    background: '#FFFFFF',
    borderRadius: 16,
    padding:    '16px 20px',
    boxShadow:  '0 2px 12px rgba(27,67,50,0.08)',
  },
  countItemWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center' },

  // Options grid (shared)
  optionsGrid: {
    display:             'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap:                 12,
    width:               '100%',
  },
  optionBtn: {
    padding:      '18px 12px',
    fontSize:     '1.15rem',
    fontWeight:   700,
    color:        COLORS.greenDark,
    borderRadius: 14,
    cursor:       'pointer',
    transition:   'all 0.12s ease',
    minHeight:    56,
  },

  // Pay area
  payWrap: { display: 'flex', flexDirection: 'column', gap: 20 },
  payWrapLandscape: {
    display:  'grid',
    gridTemplateColumns: '1fr 1fr',
    gap:      20,
    alignItems: 'start',
  },
  payLandscapeLeft:  { display: 'flex', flexDirection: 'column', gap: 16 },
  payLandscapeRight: { display: 'flex', flexDirection: 'column' },

  // Price tag
  priceTag: {
    position:       'relative',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    background:     COLORS.greenDark,
    borderRadius:   16,
    padding:        '20px 32px',
    boxShadow:      '0 4px 20px rgba(27,67,50,0.20)',
    minHeight:      80,
  },
  priceTagHole: {
    position:     'absolute',
    left:         16,
    width:        18,
    height:       18,
    borderRadius: '50%',
    background:   COLORS.bgPage,
    border:       `2px solid ${COLORS.borderWarm}`,
  },
  priceAmount: {
    fontSize:    '2.4rem',
    fontWeight:  800,
    color:       '#FFFFFF',
    letterSpacing: '-0.02em',
  },

  // Wallet
  walletGrid: {
    display:             'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
    gap:                 10,
  },
  walletBtn: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            4,
    padding:        '10px 8px',
    background:     '#FFFFFF',
    border:         `2px solid ${COLORS.borderWarm}`,
    borderRadius:   14,
    cursor:         'pointer',
    transition:     'all 0.12s ease',
    minHeight:      48,
  },
  walletBtnLabel: { fontSize: '0.875rem', fontWeight: 700, color: COLORS.greenDark },

  // Dish
  dish: {
    background:   '#FFFFFF',
    borderRadius: 16,
    border:       '2px solid #C0A882',
    padding:      '12px 16px',
    transition:   'border-color 0.25s ease',
  },
  dishHeader: {
    display:     'flex',
    alignItems:  'center',
    gap:         8,
    marginBottom: 10,
    flexWrap:    'wrap',
  },
  dishLabel: { fontWeight: 700, color: COLORS.greenDark, fontSize: '1rem', flex: 1 },
  dishTotal: { fontSize: '0.95rem', color: '#6B4C3B' },
  clearBtn: {
    fontSize: '0.875rem',
    fontWeight:   700,
    color:        '#E63946',
    background:   '#FFE0E3',
    border:       'none',
    borderRadius: 8,
    padding:      '6px 12px',
    cursor:       'pointer',
    minHeight:    32,
  },
  dishEmpty: { color: '#B0A090', fontSize: '1.1rem', textAlign: 'center', margin: '8px 0' },
  dishItems: {
    display:  'flex',
    flexWrap: 'wrap',
    gap:      8,
  },
  dishItem: {
    position:      'relative',
    display:       'flex',
    flexDirection: 'column',
    alignItems:    'center',
    gap:           2,
    padding:       '8px',
    background:    COLORS.bgPage,
    border:        `1.5px solid ${COLORS.borderWarm}`,
    borderRadius:  12,
    cursor:        'pointer',
    minHeight:     48,
    minWidth:      60,
  },
  dishItemLabel:  { fontSize: '0.875rem', fontWeight: 700, color: COLORS.greenDark },
  dishItemRemove: { fontSize: '0.875rem', color: '#E63946', position: 'absolute', top: 2, right: 4 },

  // Mode selector
  modeEmoji: { fontSize: '1.6rem', lineHeight: 1 },
  modeLabel: { fontSize: '1rem', fontWeight: 700, color: COLORS.greenDark },
  modeDesc:  { fontSize: '0.875rem', color: '#6B4C3B', lineHeight: 1.4 },

  // Complete screen
  completeRoot: {
    minHeight:      '100dvh',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    background:     COLORS.bgPage,
    padding:        '24px 16px',
  },
  completeCard: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            16,
    background:     '#FFFFFF',
    borderRadius:   24,
    padding:        '36px 28px',
    boxShadow:      '0 8px 40px rgba(27,67,50,0.12)',
    maxWidth:       440,
    width:          '100%',
  },
  completeEmoji:    { fontSize: '3rem' },
  completeTitle:    { fontSize: '2rem', fontWeight: 800, color: COLORS.greenDark, textAlign: 'center', margin: 0 },
  completeSubtitle: { fontSize: '1.05rem', color: '#6B4C3B', textAlign: 'center', margin: 0 },

  statsGrid: {
    display:             'grid',
    gridTemplateColumns: '1fr 1fr',
    gap:                 12,
    width:               '100%',
  },
  statCell: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    gap:            2,
    background:     '#F5EDE4',
    borderRadius:   12,
    padding:        '12px 8px',
  },
  statValue: { fontSize: '1.3rem', fontWeight: 800, color: COLORS.greenDark },
  statLabel: { fontSize: '0.875rem', fontWeight: 600, color: '#9A8070' },

  completeBtns: { display: 'flex', flexDirection: 'column', gap: 10, width: '100%' },
  primaryBtn: {
    padding:      '18px',
    fontSize:     '1.1rem',
    fontWeight:   800,
    color:        '#FFFFFF',
    background:   COLORS.gradientPrimary,
    border:       'none',
    borderRadius: 14,
    cursor:       'pointer',
    minHeight:    56,
  },
  secondaryBtn: {
    padding:      '16px',
    fontSize:     '1rem',
    fontWeight:   700,
    color:        COLORS.green,
    background:   '#F0FAF4',
    border:       `2px solid ${COLORS.green}`,
    borderRadius: 14,
    cursor:       'pointer',
    minHeight:    52,
  },
  ghostBtn: {
    padding:      '14px',
    fontSize:     '1rem',
    fontWeight:   700,
    color:        '#9A8070',
    background:   'transparent',
    border:       '1.5px solid #D8C9B8',
    borderRadius: 14,
    cursor:       'pointer',
    minHeight:    48,
  },
}
